import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiChevronDown, FiFilter, FiHeart, FiSearch, FiX } from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/shop.scss";

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [selectedCategories, setSelectedCategories] = useState(() =>
    searchParams.get("category") ? [searchParams.get("category")] : []
  );
  const [selectedSizes, setSelectedSizes] = useState(() =>
    searchParams.get("size") ? [searchParams.get("size")] : []
  );
  const [maxPrice, setMaxPrice] = useState(250);
  const [sortBy, setSortBy] = useState("featured");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toggleWishlist, isWishlisted } = useContext(CartContext);

  const categories = [...new Set(productData.map((product) => product.category))];
  const sizes = [...new Set(productData.flatMap((product) => product.size || []))];

  useEffect(() => {
    const category = searchParams.get("category");
    const size = searchParams.get("size");
    const search = searchParams.get("search") || "";
    setSelectedCategories(category ? [category] : []);
    setSelectedSizes(size ? [size] : []);
    setSearchInput(search);
    setIsLoading(true);
    const timeout = window.setTimeout(() => setIsLoading(false), 180);
    return () => window.clearTimeout(timeout);
  }, [searchParams]);

  const visibleProducts = useMemo(() => {
    const query = (searchParams.get("search") || "").trim().toLowerCase();
    const collection = searchParams.get("collection");
    const tag = searchParams.get("tag");

    const filtered = productData.filter((product) => {
      const searchable = [
        product.name,
        product.category,
        product.description?.join?.(" ") || product.description,
        product.tags?.join(" "),
      ]
        .join(" ")
        .toLowerCase();

      return (
        (!query || searchable.includes(query)) &&
        (!selectedCategories.length || selectedCategories.includes(product.category)) &&
        (!selectedSizes.length || selectedSizes.some((size) => product.size?.includes(size))) &&
        Number(product.price) <= maxPrice &&
        (!collection || collection !== "new" || product.tags?.includes("new")) &&
        (!tag || product.tags?.includes(tag))
      );
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "price-low") return Number(a.price) - Number(b.price);
      if (sortBy === "price-high") return Number(b.price) - Number(a.price);
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "newest") return b.id - a.id;
      return Number(Boolean(b.tags?.includes("best-seller"))) - Number(Boolean(a.tags?.includes("best-seller")));
    });
  }, [maxPrice, searchParams, selectedCategories, selectedSizes, sortBy]);

  const toggleFilter = (value, selected, setSelected) => {
    setSelected(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value]
    );
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) next.set("search", searchInput.trim());
    else next.delete("search");
    setSearchParams(next);
  };

  const resetFilters = () => {
    setSelectedCategories([]);
    setSelectedSizes([]);
    setMaxPrice(250);
    setSearchInput("");
    setSortBy("featured");
    setSearchParams({});
  };

  const money = (value) =>
    new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
      style: "currency",
      currency: siteConfig.currency?.code || "USD",
    }).format(Number(value));

  return (
    <main className="shop" id="main-content">
      <header className="shop-hero">
        <p className="eyebrow">THE COMPLETE COLLECTION</p>
        <h1>{searchParams.get("category") || "SHOP ALL"}</h1>
        <p>
          Build a planning practice with tactile tools designed for focus,
          flexibility, and calm.
        </p>
      </header>

      <div className="shop-layout page-width">
        <div className="shop-toolbar">
          <button
            type="button"
            className="shop-filter-trigger"
            onClick={() => setFiltersOpen(true)}
          >
            <FiFilter /> FILTER
          </button>
          <span>{visibleProducts.length} PRODUCTS</span>
          <label>
            <span>SORT BY</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="price-low">Price, low to high</option>
              <option value="price-high">Price, high to low</option>
              <option value="name">Alphabetically, A–Z</option>
            </select>
            <FiChevronDown />
          </label>
        </div>

        <div className="shop-content">
          <div
            className={`shop-filter-backdrop ${filtersOpen ? "is-open" : ""}`}
            onClick={() => setFiltersOpen(false)}
            aria-hidden="true"
          />
          <aside className={`shop-filters ${filtersOpen ? "is-open" : ""}`}>
            <div className="shop-filters__heading">
              <span>FILTERS</span>
              <button type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters">
                <FiX />
              </button>
            </div>

            <form className="shop-search" onSubmit={submitSearch} role="search">
              <label htmlFor="collection-search">Search the collection</label>
              <div>
                <input
                  id="collection-search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search"
                />
                <button type="submit" aria-label="Search products"><FiSearch /></button>
              </div>
            </form>

            <details open>
              <summary>PRODUCT TYPE <FiChevronDown /></summary>
              <div className="filter-options">
                {categories.map((category) => (
                  <label key={category}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() => toggleFilter(category, selectedCategories, setSelectedCategories)}
                    />
                    <span>{category}</span>
                    <small>{productData.filter((product) => product.category === category).length}</small>
                  </label>
                ))}
              </div>
            </details>

            <details open>
              <summary>SIZE <FiChevronDown /></summary>
              <div className="filter-options filter-options--sizes">
                {sizes.map((size) => (
                  <label key={size}>
                    <input
                      type="checkbox"
                      checked={selectedSizes.includes(size)}
                      onChange={() => toggleFilter(size, selectedSizes, setSelectedSizes)}
                    />
                    <span>{size}</span>
                  </label>
                ))}
              </div>
            </details>

            <details open>
              <summary>PRICE <FiChevronDown /></summary>
              <div className="price-filter">
                <div><span>{money(0)}</span><span>{money(maxPrice)}</span></div>
                <input
                  type="range"
                  min="10"
                  max="250"
                  step="5"
                  value={maxPrice}
                  onChange={(event) => setMaxPrice(Number(event.target.value))}
                  aria-label="Maximum price"
                />
              </div>
            </details>

            <button type="button" className="filter-reset" onClick={resetFilters}>
              CLEAR ALL FILTERS
            </button>
          </aside>

          <section className="shop-results" aria-live="polite" aria-busy={isLoading}>
            {isLoading ? (
              <div className="shop-skeleton" aria-label="Loading products">
                {Array.from({ length: 8 }).map((_, index) => <span key={index} />)}
              </div>
            ) : visibleProducts.length > 0 ? (
              <div className="product-card-grid shop-product-grid">
                {visibleProducts.map((product) => (
                  <article className="product-card" key={product.id}>
                    <div className="product-card__media">
                      <Link to={`/shopDetails/${product.id}`} aria-label={`View ${product.name}`}>
                        <img src={product.image} alt={product.name} loading="lazy" />
                      </Link>
                      {product.tags?.includes("new") && <span className="product-badge">NEW</span>}
                      {product.tags?.includes("sale") && <span className="product-badge product-badge--sale">SALE</span>}
                      <button
                        type="button"
                        className={`product-card__wish ${isWishlisted(product.id) ? "is-active" : ""}`}
                        onClick={() => toggleWishlist(product.id)}
                        aria-label={isWishlisted(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                      >
                        <FiHeart />
                      </button>
                      <Link className="product-card__quick" to={`/shopDetails/${product.id}`}>
                        CHOOSE OPTIONS
                      </Link>
                    </div>
                    <div className="product-card__details">
                      <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                      <span>{money(product.price)}</span>
                      <small>{product.colors?.length || 1} options available</small>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="shop-empty">
                <p className="eyebrow">NO RESULTS</p>
                <h2>Nothing matched those details.</h2>
                <p>Try a broader search or clear the filters to view the full collection.</p>
                <button type="button" className="button button--dark" onClick={resetFilters}>
                  RESET FILTERS
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

export default Shop;
